import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { TFunction } from 'i18next';
import { MemberRole, type Member } from '../../types';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import { useUpdateMember } from '../../hooks/useUpdateMember';
import { useToast } from '@/shared/hooks/useToast';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Calendar } from '@/shared/components/ui/Calendar';
import { cn } from '@/core/lib/utils';

interface ExpirationCellProps {
  member: Member;
  t: TFunction;
}

export function ExpirationCell({ member, t }: ExpirationCellProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const formatInstant = useInstantFormat();
  const { mutate: updateMember, isPending } = useUpdateMember();
  const { toast } = useToast();

  const isEditable = member.role !== MemberRole.Owner && !member.isCurrentUser;
  const isDisabled = !isEditable || isPending;

  const selectedDate = member.expiresAt ? new Date(member.expiresAt) : undefined;

  const handleSelect = (date: Date | undefined) => {
    const value = date ? date.toISOString() : null;
    if (value === member.expiresAt) {
      setIsOpen(false);
      return;
    }
    updateMember(
      { memberId: member.id, payload: { expiresAt: value } },
      {
        onSuccess: () => {
          setIsOpen(false);
          toast.success({
            title: t('toast.expirationUpdated'),
            description: t('toast.expirationUpdatedDesc', { name: member.displayName }),
          });
        },
      },
    );
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!member.expiresAt) return;
    updateMember(
      { memberId: member.id, payload: { expiresAt: null } },
      {
        onSuccess: () =>
          toast.success({
            title: t('toast.expirationUpdated'),
            description: t('toast.expirationUpdatedDesc', { name: member.displayName }),
          }),
      },
    );
  };

  return (
    <div data-testid={`members-expiration-cell-${member.id}`}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={isDisabled}
            data-testid="expiration-trigger"
            data-state={isOpen ? 'open' : 'closed'}
            className={cn(
              'flex w-auto items-center gap-1 h-auto p-0 border-0 shadow-none',
              'bg-transparent hover:bg-transparent',
              'ring-0 focus-visible:outline-none focus-visible:ring-0',
              'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              {member.expiresAt ? (
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                  {formatInstant(member.expiresAt)}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">{t('expiration.none')}</span>
              )}
            </div>
            {!isDisabled && (
              <div className="flex items-center gap-1 shrink-0">
                {member.expiresAt && (
                  <span
                    role="button"
                    tabIndex={-1}
                    data-testid="expiration-clear"
                    onClick={handleClear}
                    className="p-0.5 rounded-full text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    'w-4 h-4 text-muted-foreground transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </div>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            selected={selectedDate}
            onSelect={handleSelect}
            fromDate={new Date()}
            mode="single"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
